import type { GoonDeckFoil } from "./goondeck-rules"

// The full tunable foil recipe — this is the shape both the admin editor
// and the real card renderer read/write. Kept as one JSON blob (not a
// migration per field) so new knobs can be added later without a schema
// change; sanitizeFoilPresetConfig() is the single place that has to know
// about every field and its valid range.
export const FOIL_PATTERN_TYPES = ["facet", "facet2", "fineFacet", "dots", "stars", "grain", "rain", "glitch", "glisten", "none"] as const
export type FoilPatternType = (typeof FOIL_PATTERN_TYPES)[number]

export const GRAIN_TYPES = ["uniform", "gaussian"] as const
export type GrainType = (typeof GRAIN_TYPES)[number]

// "full" selects every pixel in the target region unconditionally — needed
// for effects like Threshold that should process the whole art/region, not
// just a selective subset the way edges/dark/bright/color pick pixels out.
export const HARD_MASK_MODES = ["off", "edges", "dark", "bright", "color", "full"] as const
export type HardMaskMode = (typeof HARD_MASK_MODES)[number]

export const HARD_MASK_EFFECTS = ["rainbow", "polarized", "oil", "gold", "custom", "invert", "threshold"] as const
export type HardMaskEffect = (typeof HARD_MASK_EFFECTS)[number]

export const HARD_MASK_TARGETS = ["full", "art", "layout", "border", "edge", "pattern"] as const
export type HardMaskTarget = (typeof HARD_MASK_TARGETS)[number]

// Mask "shapes" — an independent second control on top of the layer
// effects above. A layer effect (gold/iridescent/glassSweep/etc.) normally
// washes evenly across its whole target region; a mask shape constrains
// *where* that effect actually shows through, so e.g. a gold pattern effect
// can show only inside a moving spotlight instead of across the whole card.
export const FOIL_MASK_SHAPES = ["none", "radial", "glintThin", "glintThick", "glareSweep", "sparkle"] as const
export type FoilMaskShape = (typeof FOIL_MASK_SHAPES)[number]

// How the mask shape's position moves. "mirror" tracks the same
// opposite-of-cursor point glassSweep uses. "reverseMirror" is the flip of
// that — it tracks the cursor directly, same point glow/specular's own
// content already uses, which matters because a mask and the effect it's
// masking need to move the SAME way to line up: pairing an inverted-motion
// mask with a cursor-following effect (or vice versa) puts the mask's
// window and the effect's bright spot on opposite sides of the card, so the
// combo looks like it's barely doing anything. "random" re-rolls the shape
// to a new random position/angle every time the cursor moves, instead of
// tracking it at all — this is what creates the "artificial sparkle" look
// (real glints don't move in lockstep with your eye, they just catch and
// vanish), rather than a predictable sweep.
export const FOIL_MASK_MOTIONS = ["mirror", "reverseMirror", "random"] as const
export type FoilMaskMotion = (typeof FOIL_MASK_MOTIONS)[number]

export interface FoilMaskConfig {
  shape: FoilMaskShape
  motion: FoilMaskMotion
}

export const DEFAULT_FOIL_MASK: FoilMaskConfig = { shape: "none", motion: "mirror" }

export interface GoonDeckHardMaskConfig {
  mode: HardMaskMode
  effect: HardMaskEffect
  target: HardMaskTarget
  color: string
  intensity: number
}

// Tilt frames — the card's own art frames swap as you rotate the card, one
// pie slice of tilt angle each, instead of the art being one fixed picture.
// Shares the mechanic behind Cast Reveal / cover media tilt slots (see
// components/goonpals/tilt-reveal.tsx); this block is only the *behavior*,
// since the frame images themselves are per-card art and live on the card.
//
// "off" is every existing preset — this is a no-op until an admin turns it
// on. Deliberately mutually exclusive with the hard masks: those cache the
// card art's pixel data and only recompute on resize, so art that swaps per
// slice would force a full getImageData pass on every slice change. The
// renderer drops the hard masks whenever this is "frames".
export const TILT_FRAME_MODES = ["off", "frames"] as const
export type TiltFrameMode = (typeof TILT_FRAME_MODES)[number]

export interface GoonDeckTiltFramesConfig {
  mode: TiltFrameMode
  /** How far out from center (0-1, 1 = the card edge) before frames start
   * showing, and how much further until fully shown. */
  deadZone: number
  feather: number
  /** Crossfade between frames. Near 0 is a hard cut — what frame-by-frame
   * animation wants; higher reads as a dissolve between separate pictures. */
  crossfadeMs: number
  /** Blur a frame enters with before sharpening. 0 keeps frames crisp. */
  entranceBlur: number
}

export const DEFAULT_TILT_FRAMES: GoonDeckTiltFramesConfig = {
  mode: "off",
  deadZone: 0.35,
  feather: 0.25,
  // Animation-friendly out of the box: a near-hard cut with no entrance
  // blur, since the headline use is flipbook frames rather than the slow
  // character dissolve the discovery card uses.
  crossfadeMs: 90,
  entranceBlur: 0,
}

export interface GoonDeckPostEffectsConfig {
  artBrightness: number
  artContrast: number
  artEmboss: number
  artEmbossMask: FoilMaskConfig
  artEmbossMode: "edges" | "color" | "dark" | "bright" | "luma"
  artEmbossColor: string
  artEmbossColorTolerance: number
  artEmbossDirection: number
  artEmbossThreshold: number
  artEmbossLumaMin: number
  artEmbossLumaMax: number
}

export const DEFAULT_HARD_MASK: GoonDeckHardMaskConfig = {
  mode: "off",
  effect: "rainbow",
  target: "full",
  color: "#f0d535",
  intensity: 1,
}

export const DEFAULT_POST_EFFECTS: GoonDeckPostEffectsConfig = {
  artBrightness: 1,
  artContrast: 1,
  artEmboss: 0,
  artEmbossMask: DEFAULT_FOIL_MASK,
  artEmbossMode: "edges",
  artEmbossColor: "#ffffff",
  artEmbossColorTolerance: 0.35,
  artEmbossDirection: 45,
  artEmbossThreshold: 0.35,
  artEmbossLumaMin: 0.25,
  artEmbossLumaMax: 0.75,
}

export const POST_EFFECT_EMBOSS_MODES = ["edges", "color", "dark", "bright", "luma"] as const

export const FOIL_LAYER_EFFECTS = [
  "none",
  "iridescent",
  "oilSlick",
  "polarization",
  "gold",
  "edgeRainbow",
  "grain",
  "stars",
  "starRainbow",
  "rain",
  // A bright glint that sits at the OPPOSITE point from the cursor/tilt
  // (not following it, like the always-on top glare does) plus a moving
  // diagonal highlight band on the same inverted angle — reads as a sparkle
  // catching light away from where you're actually looking/tilting, rather
  // than a highlight glued to the cursor. Available on any of the four
  // regions (pattern/art/layout/edge), same as every other layer effect.
  "glassSweep",
  // Brightness-only effects — no hue shift at all (pure white, "screen"
  // blended), so they brighten whatever pattern/art is underneath instead
  // of tinting it toward a color like every effect above does.
  "glow",
  "specular",
  // Invert/Threshold used to live here via CSS backdrop-filter, but that
  // reliably captured the wrong backdrop (the already-blended stack of
  // other overlay layers, not the plain card art) no matter where in the
  // DOM it was rendered, and testing showed it not visibly affecting the
  // art at all even at full strength. Moved to real per-pixel canvas
  // processing instead — see HARD_MASK_EFFECTS below, which already had a
  // working "invert" for exactly this reason.
] as const
export type FoilLayerEffect = (typeof FOIL_LAYER_EFFECTS)[number]

// Vector card-frame templates for the "layout" layer effect — real
// Pokemon/playing-card-style borders + info-plate shapes (built as SVG masks
// in goondeck-foil-render.ts) instead of the old plain padding-ring hack, so
// the layout effect reads as an actual card design instead of a mystery
// colored blob with square inner corners. "none" keeps the layout effect
// off entirely (or falls back to a plain ring for old presets).
export const FOIL_LAYOUT_PATTERNS = [
  "none",
  "classicRing",
  "dualLine",
  "brackets",
  "plaque",
  "notched",
  "infoPanel",
  "diamond",
  "circuit",
  "arcTop",
  // These three are built around the actual GoonDeck card fields (name,
  // type/attribute/range, HP/ATK/Shield) rather than being purely
  // decorative — the panel shapes reserve real space for that data instead
  // of an arbitrary "info area" a stat overlay would have to work around.
  "statRow",
  "pokemonFull",
  "sideRail",
] as const
export type FoilLayoutPatternType = (typeof FOIL_LAYOUT_PATTERNS)[number]

export interface GoonDeckFoilTunes {
  base: number
  color: number
  pattern: number
  patternGrip: number
  grainStrength: number
  /** How big each noise speck reads as — lower = fine/tight grain, higher =
   * coarse/chunky grain. 1 = the shipped default. Independent of
   * grainStrength, which only controls how visible/opaque the grain is. */
  grainSize: number
  /** Same idea as grainStrength/grainSize, but for the "Subtle glisten"
   * pattern specifically — it's independent of the shared `pattern` tune so
   * it can be dialed up without also boosting facet/dots/stars strength. */
  glistenStrength: number
  glistenSize: number
  shine: number
  sweep: number
  sweepSpread: number
  sparkle: number
  edge: number
  /** Per-region opacity multipliers for the art/layout/edge layer effects
   * (config.layerEffects.{art,layout,edge}) — separate from `edge` above
   * (which is a legacy edge-only multiplier, kept for old presets) so each
   * of the three regions can be dialed independently instead of only the
   * edge one being tunable. */
  artEffectOpacity: number
  layoutEffectOpacity: number
  /** Same idea, for the pattern's own color fill (the facet/dots/stars/rain
   * mask) — lets a pattern be pushed toward "gold", "iridescent", etc.
   * instead of only ever showing the plain colorA/colorB sweep. */
  patternEffectOpacity: number
  /** Dedicated strength multipliers for the "glow"/"specular" layer
   * effects specifically — these are pure-white brightness effects with no
   * inherent color intensity to lean on (every other effect gets its
   * strength partly from saturated gradient colors), so the shared
   * artEffectOpacity/etc. sliders alone weren't enough to make them read as
   * strongly as the colored effects. Applies on top of whichever region
   * opacity (pattern/art/layout/edge) the effect is actually assigned to. */
  glowStrength: number
  specularStrength: number
  /** The always-on cursor-following gloss highlight every card has,
   * independent of any layer effect — this is NOT the "Glow" layer effect
   * and isn't affected by its mask/motion/strength settings at all. It's
   * easy to mistake this baseline shine for the Glow effect since both are
   * a white cursor-following highlight; this lets it be turned down/off so
   * the Glow effect's own behavior is easier to see in isolation. */
  topGlareOpacity: number
  /** Scales how big the mask "shape" window is (radial spotlight radius,
   * glint/glare band width, sparkle field size) — shared across all four
   * mask targets rather than one knob each, since it's the same underlying
   * "how strong/big" question for every shape. 1 = the shipped default
   * size. */
  maskSize: number
  gold: number
  polarization: number
  ink: number
  edgeFeather: number
  revealFloor: number
  revealStrength: number
  fadeWidth: number
  overlayOpacity: number
}

export interface GoonDeckFoilPresetConfig {
  baseColors: [string, string, string]
  colorA: string
  colorB: string
  /** Color the "glow" layer effect uses instead of plain white — lets a
   * glow read as a warm/cool/tinted light source rather than always being
   * a neutral white brightening wash. Defaults to white, which reproduces
   * the original behavior exactly. Specular stays pure white on purpose —
   * it's meant to read as a hard reflective hotspot, not a colored light. */
  glowColor: string
  /** Color of the always-on cursor-following highlight every card has (the
   * "Shine / glare" and "Always-on top glare" layers) — separate from
   * glowColor above, which only tints the optional "Glow" layer effect.
   * Defaults to white, reproducing the original behavior exactly. */
  cursorGlowColor: string
  pattern: FoilPatternType
  grainType: GrainType
  /** Which vector card-frame template the "layout" layer effect is masked
   * to. Only matters when layerEffects.layout !== "none". */
  layoutPattern: FoilLayoutPatternType
  layerEffects: {
    /** Applies to the pattern's own color fill (the facet/dots/stars/rain
     * mask's conic-gradient sweep) — e.g. set to "gold" to make a facet
     * pattern actually read as cut gold instead of the plain colorA/colorB
     * sweep, or "iridescent" for a shifting rainbow pattern. */
    pattern: FoilLayerEffect
    art: FoilLayerEffect
    /** Applies to the card's border ring and any info/stat areas laid over
     * the face — everything that isn't the art or the outer edge line. */
    layout: FoilLayerEffect
    edge: FoilLayerEffect
  }
  /** Optional mask shape constraining WHERE each region's layer effect
   * shows through — independent of layerEffects above, which only controls
   * WHAT the effect looks like. "none" leaves the effect washing evenly
   * across its whole region, same as before this existed. */
  masks: {
    pattern: FoilMaskConfig
    art: FoilMaskConfig
    layout: FoilMaskConfig
    edge: FoilMaskConfig
  }
  /** Real per-pixel edge/luminance detection off the card's actual art —
   * expensive (canvas + a full-image pixel pass per pointer move), so the
   * renderer only ever mounts this in the single-card inspect view, never
   * in a grid of cards. See components/goonpals/hard-mask-canvas.tsx. */
  hardMask: GoonDeckHardMaskConfig
  /** A second, independent hard mask — e.g. Detected edges + Rainbow on
   * one, Full region + Threshold on the other, layered together. Off by
   * default so it's a no-op for every preset until an admin opts in. */
  hardMask2: GoonDeckHardMaskConfig
  /** Tilt-driven art frame swapping. Mutually exclusive with the hard masks
   * above — see GoonDeckTiltFramesConfig. Frame images live on the card. */
  tiltFrames: GoonDeckTiltFramesConfig
  /** Final art-surface processing that happens outside the regular foil
   * layer stack. This is deliberately separate from layerEffects because it
   * changes how the printed art itself reads before/under the foil, rather
   * than adding another holo material layer. */
  postEffects: GoonDeckPostEffectsConfig
  tunes: GoonDeckFoilTunes
}

export interface GoonDeckFoilPreset {
  id: string
  name: string
  foilTier: GoonDeckFoil
  isActive: boolean
  config: GoonDeckFoilPresetConfig
  createdAt: string
  updatedAt: string
}

export const DEFAULT_FOIL_TUNES: GoonDeckFoilTunes = {
  base: 1,
  color: 1,
  pattern: 1,
  // Off by default for every tier — the tilt-tracking pattern-pan effect
  // was making the texture swim distractingly. Presets that want it can
  // still dial it back up per-preset in the admin editor.
  patternGrip: 0,
  grainStrength: 1,
  grainSize: 1,
  glistenStrength: 1,
  glistenSize: 1,
  shine: 1,
  sweep: 1,
  sweepSpread: 1,
  sparkle: 1,
  edge: 1,
  artEffectOpacity: 1,
  layoutEffectOpacity: 1,
  patternEffectOpacity: 1,
  // Default well above 1 — glow/specular's own recipe opacity is
  // deliberately modest (see layerEffectRecipe), these are what actually
  // make them read clearly rather than needing every preset to crank a
  // slider just to see them at all.
  glowStrength: 1.8,
  specularStrength: 2,
  topGlareOpacity: 1,
  maskSize: 1,
  gold: 1,
  polarization: 1,
  ink: 1,
  edgeFeather: 1,
  revealFloor: 0.1,
  revealStrength: 0.9,
  fadeWidth: 1,
  overlayOpacity: 0.92,
}

// Base seed colors per tier — the starting point an admin lands on the
// first time they open a tier that has no saved preset yet. Matches the
// palette we already shipped, so switching to the preset system doesn't
// change how any card looks until someone actually edits a preset.
export const DEFAULT_FOIL_CONFIG_BY_TIER: Record<GoonDeckFoil, GoonDeckFoilPresetConfig> = {
  standard: {
    baseColors: ["#ffffff", "#f4f4f5", "#e4e4e7"],
    colorA: "#ffffff",
    colorB: "#e4e4e7",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "none",
    grainType: "uniform",
    layoutPattern: "classicRing",
    layerEffects: { pattern: "none", art: "none", layout: "none", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 0, pattern: 0 },
  },
  silver: {
    baseColors: ["#b8c3d4", "#ffffff", "#dce1ec"],
    colorA: "#e9fbff",
    colorB: "#aeb8cc",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "fineFacet",
    grainType: "uniform",
    layoutPattern: "dualLine",
    layerEffects: { pattern: "polarization", art: "none", layout: "polarization", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 0.6 },
  },
  gold: {
    baseColors: ["#bb8429", "#fff0a7", "#f0a92e"],
    colorA: "#ffe274",
    colorB: "#ff9f2f",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "facet",
    grainType: "gaussian",
    layoutPattern: "plaque",
    layerEffects: { pattern: "gold", art: "gold", layout: "polarization", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 0.7, gold: 1.2, shine: 0.55, edgeFeather: 0.5, overlayOpacity: 0.78 },
  },
  holo: {
    baseColors: ["#55f8ff", "#fff9c8", "#d15cff"],
    colorA: "#00f5ff",
    colorB: "#ff50e8",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "facet",
    grainType: "uniform",
    layoutPattern: "brackets",
    layerEffects: { pattern: "iridescent", art: "none", layout: "iridescent", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES },
  },
  rainbow: {
    baseColors: ["#ff4d6d", "#fff66d", "#42e7ff"],
    colorA: "#ff3162",
    colorB: "#27e8ff",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "facet",
    grainType: "uniform",
    layoutPattern: "diamond",
    layerEffects: { pattern: "edgeRainbow", art: "none", layout: "edgeRainbow", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 1.1 },
  },
  starfield: {
    baseColors: ["#dce6ff", "#ffffff", "#6ea8ff"],
    colorA: "#ffffff",
    colorB: "#6ea8ff",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "stars",
    grainType: "uniform",
    layoutPattern: "arcTop",
    layerEffects: { pattern: "stars", art: "stars", layout: "iridescent", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, sparkle: 1.4 },
  },
  glitch: {
    baseColors: ["#0ff0ff", "#111116", "#ff2bd6"],
    colorA: "#00ffff",
    colorB: "#ff00b2",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "glitch",
    grainType: "uniform",
    layoutPattern: "circuit",
    layerEffects: { pattern: "none", art: "none", layout: "grain", edge: "edgeRainbow" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, edge: 1.4 },
  },
  prismatic: {
    baseColors: ["#ffffff", "#44ffe1", "#ffd45c"],
    colorA: "#ffffff",
    colorB: "#ffd45c",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "facet",
    grainType: "gaussian",
    layoutPattern: "infoPanel",
    layerEffects: { pattern: "iridescent", art: "iridescent", layout: "polarization", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 1.15 },
  },
  mythic: {
    baseColors: ["#ffd36d", "#fff9ce", "#a66dff"],
    colorA: "#ffe067",
    colorB: "#9d6bff",
    glowColor: "#ffffff",
    cursorGlowColor: "#ffffff",
    pattern: "facet",
    grainType: "gaussian",
    layoutPattern: "notched",
    layerEffects: { pattern: "oilSlick", art: "oilSlick", layout: "edgeRainbow", edge: "none" },
    masks: { pattern: DEFAULT_FOIL_MASK, art: DEFAULT_FOIL_MASK, layout: DEFAULT_FOIL_MASK, edge: DEFAULT_FOIL_MASK },
    hardMask: DEFAULT_HARD_MASK,
    hardMask2: DEFAULT_HARD_MASK,
    tiltFrames: DEFAULT_TILT_FRAMES,
    postEffects: DEFAULT_POST_EFFECTS,
    tunes: { ...DEFAULT_FOIL_TUNES, color: 1.2, edge: 1.2 },
  },
}

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const num = typeof value === "number" && Number.isFinite(value) ? value : fallback
  return Math.min(max, Math.max(min, num))
}

function sanitizeColor(value: unknown, fallback: string): string {
  return typeof value === "string" && /^#[0-9a-fA-F]{3,8}$/.test(value.trim()) ? value.trim() : fallback
}

function sanitizeEnum<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return typeof value === "string" && (options as readonly string[]).includes(value) ? (value as T) : fallback
}

/** Fills in/clamps every field so a partial or malformed admin submission
 * (or an old preset missing newer fields) never produces a config the
 * renderer can crash on. This is the only place that needs updating when a
 * new tunable is added. */
export function sanitizeFoilPresetConfig(input: unknown, fallback: GoonDeckFoilPresetConfig): GoonDeckFoilPresetConfig {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, any>
  const rawTunes = (raw.tunes && typeof raw.tunes === "object" ? raw.tunes : {}) as Record<string, any>
  const rawLayerEffects = (raw.layerEffects && typeof raw.layerEffects === "object" ? raw.layerEffects : {}) as Record<string, any>
  const rawHardMask = (raw.hardMask && typeof raw.hardMask === "object" ? raw.hardMask : {}) as Record<string, any>
  const rawHardMask2 = (raw.hardMask2 && typeof raw.hardMask2 === "object" ? raw.hardMask2 : {}) as Record<string, any>
  const rawPostEffects = (raw.postEffects && typeof raw.postEffects === "object" ? raw.postEffects : {}) as Record<string, any>
  const rawTiltFrames = (raw.tiltFrames && typeof raw.tiltFrames === "object" ? raw.tiltFrames : {}) as Record<string, any>
  const rawMasks = (raw.masks && typeof raw.masks === "object" ? raw.masks : {}) as Record<string, any>
  const rawBase = Array.isArray(raw.baseColors) ? raw.baseColors : []

  const sanitizeMask = (value: unknown, fallback: FoilMaskConfig): FoilMaskConfig => {
    const rawMask = (value && typeof value === "object" ? value : {}) as Record<string, any>
    return {
      shape: sanitizeEnum(rawMask.shape, FOIL_MASK_SHAPES, fallback.shape),
      motion: sanitizeEnum(rawMask.motion, FOIL_MASK_MOTIONS, fallback.motion),
    }
  }

  const sanitizeHardMask = (value: Record<string, any>, fallback: GoonDeckHardMaskConfig): GoonDeckHardMaskConfig => ({
    mode: sanitizeEnum(value.mode, HARD_MASK_MODES, fallback.mode),
    effect: sanitizeEnum(value.effect, HARD_MASK_EFFECTS, fallback.effect),
    target: sanitizeEnum(value.target, HARD_MASK_TARGETS, fallback.target),
    color: sanitizeColor(value.color, fallback.color),
    intensity: clamp(value.intensity, 0, 2, fallback.intensity),
  })

  return {
    baseColors: [
      sanitizeColor(rawBase[0], fallback.baseColors[0]),
      sanitizeColor(rawBase[1], fallback.baseColors[1]),
      sanitizeColor(rawBase[2], fallback.baseColors[2]),
    ],
    colorA: sanitizeColor(raw.colorA, fallback.colorA),
    colorB: sanitizeColor(raw.colorB, fallback.colorB),
    glowColor: sanitizeColor(raw.glowColor, fallback.glowColor),
    cursorGlowColor: sanitizeColor(raw.cursorGlowColor, fallback.cursorGlowColor),
    pattern: sanitizeEnum(raw.pattern, FOIL_PATTERN_TYPES, fallback.pattern),
    grainType: sanitizeEnum(raw.grainType, GRAIN_TYPES, fallback.grainType),
    layoutPattern: sanitizeEnum(raw.layoutPattern, FOIL_LAYOUT_PATTERNS, fallback.layoutPattern),
    layerEffects: {
      pattern: sanitizeEnum(rawLayerEffects.pattern, FOIL_LAYER_EFFECTS, fallback.layerEffects.pattern),
      art: sanitizeEnum(rawLayerEffects.art, FOIL_LAYER_EFFECTS, fallback.layerEffects.art),
      layout: sanitizeEnum(rawLayerEffects.layout, FOIL_LAYER_EFFECTS, fallback.layerEffects.layout),
      edge: sanitizeEnum(rawLayerEffects.edge, FOIL_LAYER_EFFECTS, fallback.layerEffects.edge),
    },
    masks: {
      pattern: sanitizeMask(rawMasks.pattern, fallback.masks.pattern),
      art: sanitizeMask(rawMasks.art, fallback.masks.art),
      layout: sanitizeMask(rawMasks.layout, fallback.masks.layout),
      edge: sanitizeMask(rawMasks.edge, fallback.masks.edge),
    },
    hardMask: sanitizeHardMask(rawHardMask, fallback.hardMask),
    hardMask2: sanitizeHardMask(rawHardMask2, fallback.hardMask2),
    tiltFrames: {
      mode: sanitizeEnum(rawTiltFrames.mode, TILT_FRAME_MODES, fallback.tiltFrames.mode),
      deadZone: clamp(rawTiltFrames.deadZone, 0, 0.9, fallback.tiltFrames.deadZone),
      feather: clamp(rawTiltFrames.feather, 0.01, 1, fallback.tiltFrames.feather),
      crossfadeMs: clamp(rawTiltFrames.crossfadeMs, 0, 1200, fallback.tiltFrames.crossfadeMs),
      entranceBlur: clamp(rawTiltFrames.entranceBlur, 0, 30, fallback.tiltFrames.entranceBlur),
    },
    postEffects: {
      artBrightness: clamp(rawPostEffects.artBrightness, 0.4, 1.8, fallback.postEffects.artBrightness),
      artContrast: clamp(rawPostEffects.artContrast, 0.4, 1.8, fallback.postEffects.artContrast),
      artEmboss: clamp(rawPostEffects.artEmboss, 0, 2, fallback.postEffects.artEmboss),
      artEmbossMask: sanitizeMask(rawPostEffects.artEmbossMask, fallback.postEffects.artEmbossMask),
      artEmbossMode: sanitizeEnum(rawPostEffects.artEmbossMode, POST_EFFECT_EMBOSS_MODES, fallback.postEffects.artEmbossMode),
      artEmbossColor: sanitizeColor(rawPostEffects.artEmbossColor, fallback.postEffects.artEmbossColor),
      artEmbossColorTolerance: clamp(rawPostEffects.artEmbossColorTolerance, 0, 1, fallback.postEffects.artEmbossColorTolerance),
      artEmbossDirection: clamp(rawPostEffects.artEmbossDirection, 0, 360, fallback.postEffects.artEmbossDirection),
      artEmbossThreshold: clamp(rawPostEffects.artEmbossThreshold, 0, 1, fallback.postEffects.artEmbossThreshold),
      artEmbossLumaMin: clamp(rawPostEffects.artEmbossLumaMin, 0, 1, fallback.postEffects.artEmbossLumaMin),
      artEmbossLumaMax: clamp(rawPostEffects.artEmbossLumaMax, 0, 1, fallback.postEffects.artEmbossLumaMax),
    },
    tunes: {
      base: clamp(rawTunes.base, 0, 2, fallback.tunes.base),
      color: clamp(rawTunes.color, 0, 5, fallback.tunes.color),
      pattern: clamp(rawTunes.pattern, 0, 5, fallback.tunes.pattern),
      patternGrip: clamp(rawTunes.patternGrip, 0, 3, fallback.tunes.patternGrip),
      grainStrength: clamp(rawTunes.grainStrength, 0, 5, fallback.tunes.grainStrength),
      grainSize: clamp(rawTunes.grainSize, 0.3, 3, fallback.tunes.grainSize),
      glistenStrength: clamp(rawTunes.glistenStrength, 0, 5, fallback.tunes.glistenStrength),
      glistenSize: clamp(rawTunes.glistenSize, 0.3, 3, fallback.tunes.glistenSize),
      shine: clamp(rawTunes.shine, 0, 2, fallback.tunes.shine),
      sweep: clamp(rawTunes.sweep, 0, 2.5, fallback.tunes.sweep),
      sweepSpread: clamp(rawTunes.sweepSpread, 0.3, 3, fallback.tunes.sweepSpread),
      sparkle: clamp(rawTunes.sparkle, 0, 2.5, fallback.tunes.sparkle),
      edge: clamp(rawTunes.edge, 0, 6, fallback.tunes.edge),
      artEffectOpacity: clamp(rawTunes.artEffectOpacity, 0, 2, fallback.tunes.artEffectOpacity),
      layoutEffectOpacity: clamp(rawTunes.layoutEffectOpacity, 0, 2, fallback.tunes.layoutEffectOpacity),
      patternEffectOpacity: clamp(rawTunes.patternEffectOpacity, 0, 2, fallback.tunes.patternEffectOpacity),
      glowStrength: clamp(rawTunes.glowStrength, 0, 4, fallback.tunes.glowStrength),
      specularStrength: clamp(rawTunes.specularStrength, 0, 4, fallback.tunes.specularStrength),
      topGlareOpacity: clamp(rawTunes.topGlareOpacity, 0, 2, fallback.tunes.topGlareOpacity),
      maskSize: clamp(rawTunes.maskSize, 0.2, 3, fallback.tunes.maskSize),
      gold: clamp(rawTunes.gold, 0, 2, fallback.tunes.gold),
      polarization: clamp(rawTunes.polarization, 0, 4, fallback.tunes.polarization),
      ink: clamp(rawTunes.ink, 0, 2, fallback.tunes.ink),
      edgeFeather: clamp(rawTunes.edgeFeather, 0, 2, fallback.tunes.edgeFeather),
      revealFloor: clamp(rawTunes.revealFloor, 0, 1, fallback.tunes.revealFloor),
      revealStrength: clamp(rawTunes.revealStrength, 0, 2, fallback.tunes.revealStrength),
      fadeWidth: clamp(rawTunes.fadeWidth, 0.2, 2.5, fallback.tunes.fadeWidth),
      overlayOpacity: clamp(rawTunes.overlayOpacity, 0, 1, fallback.tunes.overlayOpacity),
    },
  }
}
